/**
 * Applies resolved branding to the live document.
 *
 * Kept separate from React so the same logic can run from the no-flash boot script's cache and
 * from the provider, and so it can be reasoned about without a component tree.
 */

import {
  BrandTokens,
  PLATFORM_BRAND,
  PLATFORM_FONT,
  deriveBrandTokens,
  cssFontFamily,
  googleFontHref,
  normaliseBrandColor,
  normaliseFont,
  tokensToCssVars,
} from './brandTokens';

/**
 * Shared with the inline boot script in public/index.html — keep the key in sync.
 *
 * Namespaced separately from cm-frontend's cache. When an organization has made no deliberate
 * choice the cached colour is that application's own platform default, and the two applications
 * do not share one (#365d7e here, #382c83 there), so a shared key would let one paint the other
 * in the wrong brand on any origin they happened to share.
 */
export const BRAND_CACHE_KEY = 'org_brand_vcon_v1';

export interface ResolvedBranding {
  color: string;
  font: string;
  tokens: BrandTokens;
}

/**
 * Cache shape. `color`/`font` are the source of truth for React; `vars`/`fontHref` are the
 * precomputed form the inline boot script in public/index.html applies directly, so that script
 * can stay a handful of lines and needs none of the derivation logic in this module.
 */
interface CachedBranding {
  organizationId: string | null;
  color: string;
  font: string;
  vars: Record<string, string>;
  fontHref: string | null;
}

/** The platform look, used for super-admins and whenever nothing else resolves. */
export function platformBranding(): ResolvedBranding {
  return {
    color: PLATFORM_BRAND,
    font: PLATFORM_FONT,
    tokens: deriveBrandTokens(PLATFORM_BRAND),
  };
}

/**
 * Turns a raw appearance payload into something safe to render.
 * Values that are absent, malformed, or the legacy form defaults fall back to the platform look.
 */
export function resolveBranding(payload: {
  font?: unknown;
  primaryColor?: unknown;
} | null | undefined): ResolvedBranding {
  // Both values are passed to each check so the legacy pair can be recognised as a pair.
  const color = normaliseBrandColor(payload?.primaryColor, payload?.font) || PLATFORM_BRAND;
  const font = normaliseFont(payload?.font, payload?.primaryColor) || PLATFORM_FONT;
  return { color, font, tokens: deriveBrandTokens(color) };
}

const FONT_LINK_ID = 'org-brand-font';

/**
 * Ensures the Google Fonts stylesheet for `font` is present, replacing any previous one.
 *
 * The prior implementation appended a fresh <link> on every change and never removed the old
 * ones, so switching fonts left the page loading every font it had ever seen.
 */
function ensureFontLoaded(font: string): void {
  const href = googleFontHref(font);
  if (!href) return;

  const existing = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;
  if (existing) {
    if (existing.href !== href) existing.href = href;
    return;
  }

  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

/**
 * Writes the brand tokens and font onto the document root.
 *
 * Everything downstream reads these custom properties, so this is the single point at which
 * branding becomes visible.
 */
export function applyBrandToDocument(branding: ResolvedBranding): void {
  const root = document.documentElement;

  /*
   * Colour and font are applied independently, and only when the organization actually chose
   * something. Writing the platform values back is NOT equivalent to leaving them alone: several
   * shades in the stylesheets were picked by hand and only approximately match what the palette
   * derives, so writing derived values would visibly shift them. Clearing lets each call site
   * fall back to the literal it has always used, which keeps the unbranded case — the large
   * majority of organizations — pixel-identical to before this feature existed.
   */
  const vars = tokensToCssVars(branding.tokens);
  const usesPlatformColor = branding.color === PLATFORM_BRAND;

  Object.keys(vars).forEach((name) => {
    if (usesPlatformColor) root.style.removeProperty(name);
    else root.style.setProperty(name, vars[name]);
  });

  if (branding.font === PLATFORM_FONT) {
    root.style.removeProperty('--app-font');
    return;
  }

  // Quoted so multi-word families are a single font-family token. Uses cssFontFamily rather
  // than the raw picker value so it always names the family that was actually loaded.
  root.style.setProperty('--app-font', `'${cssFontFamily(branding.font)}'`);

  ensureFontLoaded(branding.font);
}

/**
 * Persists the resolved branding so the next boot can paint before React mounts.
 *
 * The organization id is stored alongside it: a cache written for one tenant must not be used to
 * paint another, which matters on shared devices and when an admin switches accounts.
 */
export function cacheBranding(organizationId: string | null, branding: ResolvedBranding): void {
  // Nothing to pre-apply for an organization using the platform look, and caching it would make
  // the boot script write derived values that applyBrandToDocument deliberately clears.
  if (branding.color === PLATFORM_BRAND && branding.font === PLATFORM_FONT) {
    clearCachedBranding();
    return;
  }

  try {
    const payload: CachedBranding = {
      organizationId,
      color: branding.color,
      font: branding.font,
      vars: { ...tokensToCssVars(branding.tokens), '--app-font': `'${cssFontFamily(branding.font)}'` },
      fontHref: googleFontHref(branding.font),
    };
    window.localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Private-browsing or quota failures must never break rendering.
  }
}

export function clearCachedBranding(): void {
  try {
    window.localStorage.removeItem(BRAND_CACHE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Reads the cached branding for a given organization.
 * @returns null when nothing is cached or the cache belongs to a different organization.
 */
export function readCachedBranding(organizationId: string | null): ResolvedBranding | null {
  try {
    const raw = window.localStorage.getItem(BRAND_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as CachedBranding;
    if (organizationId && parsed.organizationId !== organizationId) return null;

    return resolveBranding({ font: parsed.font, primaryColor: parsed.color });
  } catch {
    return null;
  }
}
